import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { motion } from 'framer-motion';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaEnvelope, FaPhone, FaStore, FaMapMarkerAlt, FaFileImage, FaLocationArrow, FaCopy } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import 'react-toastify/dist/ReactToastify.css';
import { storeRegister, getCategories, getSubcategories, clearError, clearSuccessMessage, clearOtpStatus } from '../../../redux/Slices/storeAuthSlice';
import styles from './StoreRegister.module.css';


const StoreRegister = () => {
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        market_name: '',
        phone: '',
        email: '',
        password: '',
        password_confirmation: '',
        category_id: '',
        subcategory_id: '',
        address: '',
        trader_name: '',
        contact_number: '',
        logo: null,
        start_time: '08:00',
        end_time: '22:00',
        description: '',
        fcm_token: 'default_token',
        market_document: null,
        other_document: null,
        map_link: '',
        lat: '',
        lng: ''
    });

    const [fieldErrors, setFieldErrors] = useState({});
    
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [shake, setShake] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const [allowErrorToast, setAllowErrorToast] = useState(false);

    const navigate = useNavigate();
    const { loading, error, categories, subcategories, otpSent, successMessage } = useSelector((state) => state.storeAuth);

    // مسح الأخطاء وجلب الفئات
    useEffect(() => {
        dispatch(clearError());
        dispatch(clearOtpStatus()); // مسح حالة otpSent عند دخول الصفحة
        dispatch(getCategories());
        // السماح بعرض التوست للأخطاء الجديدة فقط
        setTimeout(() => {
            setAllowErrorToast(true);
        }, 100);
    }, [dispatch]);

    // جلب الفئات الفرعية عند تغيير الفئة الرئيسية
    useEffect(() => {
        if (formData.category_id) {
            dispatch(getSubcategories(formData.category_id));
        }
    }, [formData.category_id, dispatch]);

    // التعامل مع نجاح التسجيل
    useEffect(() => {
        if (otpSent && successMessage) {
            showSuccessToast(successMessage);
            setTimeout(() => {
                dispatch(clearSuccessMessage());
                navigate('/login');
            }, 1500);
        }
    }, [otpSent, successMessage, navigate, dispatch]);

    // التعامل مع الأخطاء
    useEffect(() => {
        if (error && allowErrorToast) {
            setShake(true);
            showErrorToast(error);
            setTimeout(() => setShake(false), 500);
        }
    }, [error, allowErrorToast]);

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (files) {
            setFormData(prev => ({
                ...prev,
                [name]: files[0]
            }));
        } else {
            // التحقق من حقول رقم الهاتف ومنع إدخال 0 في البداية
            if ((name === 'phone' || name === 'contact_number') && value) {
                let errorMessage = '';
                
                // منع إدخال 0 في بداية الرقم
                if (value.startsWith('0')) {
                    errorMessage = 'لا يمكن أن يبدأ رقم الهاتف بالرقم 0';
                }
                // منع إدخال أحرف غير رقمية (باستثناء + في البداية)
                else if (!/^[0-9+]*$/.test(value)) {
                    errorMessage = 'يرجى إدخال أرقام فقط';
                }
                // منع إدخال + في غير البداية
                else if (value.includes('+') && !value.startsWith('+')) {
                    errorMessage = 'يمكن وضع + في بداية الرقم فقط';
                }
                
                // تحديث أخطاء الحقل
                setFieldErrors(prev => ({
                    ...prev,
                    [name]: errorMessage
                }));
                
                // إذا كان هناك خطأ، لا نحدث البيانات
                if (errorMessage) {
                    return;
                }
            }

            setFormData(prev => ({
                ...prev,
                [name]: value
            }));

            // مسح خطأ الحقل إذا كانت القيمة صحيحة
            if (fieldErrors[name]) {
                setFieldErrors(prev => ({
                    ...prev,
                    [name]: ''
                }));
            }

            // إذا كان الحقل هو map_link، حاول استخراج الإحداثيات تلقائياً
            if (name === 'map_link' && value) {
                // التحقق من أن الرابط يبدو مكتملاً (يحتوي على http أو https أو يبدأ بـ maps)
                const isCompleteUrl = value.includes('http') || 
                                    (value.includes('maps') && value.length > 10) ||
                                    (value.includes('google') && value.length > 15) ||
                                    (value.includes('apple') && value.length > 15) ||
                                    (value.includes('bing') && value.length > 15) ||
                                    (value.includes('openstreetmap') && value.length > 20);
                
                if (isCompleteUrl) {
                    const coordinates = extractCoordinatesFromMapUrl(value);
                    if (coordinates) {
                        setFormData(prev => ({
                            ...prev,
                            lat: coordinates.lat.toString(),
                            lng: coordinates.lng.toString()
                            // لا نمسح map_link - نتركه كما هو
                        }));
                        showSuccessToast('تم استخراج الإحداثيات من رابط الخريطة تلقائياً');
                    }
                }
            }
        }
    };

    // دالة لاستخراج خط العرض والطول من رابط الخريطة
    const extractCoordinatesFromMapUrl = (url) => {
        if (!url) return null;

        try {
            // Google Maps URLs
            // https://maps.google.com/maps?q=lat,lng
            // https://www.google.com/maps/place/name/@lat,lng,zoom
            // https://maps.google.com/maps?q=lat,lng&z=zoom
            let match = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
            if (match) {
                return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
            }

            // Google Maps with q parameter
            match = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
            if (match) {
                return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
            }

            // Apple Maps URLs
            // https://maps.apple.com/?q=lat,lng
            // https://maps.apple.com/?ll=lat,lng
            match = url.match(/[?&](?:q|ll)=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
            if (match) {
                return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
            }

            // Bing Maps URLs
            // https://www.bing.com/maps?cp=lat~lng
            match = url.match(/cp=(-?\d+\.?\d*)~(-?\d+\.?\d*)/);
            if (match) {
                return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
            }

            // OpenStreetMap URLs
            // https://www.openstreetmap.org/?mlat=lat&mlon=lng
            match = url.match(/mlat=(-?\d+\.?\d*)&mlon=(-?\d+\.?\d*)/);
            if (match) {
                return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
            }

            // Generic coordinate pattern (lat,lng)
            match = url.match(/(-?\d+\.?\d*),(-?\d+\.?\d*)/);
            if (match) {
                return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
            }

            return null;
        } catch (error) {
            return null;
        }
    };

    // دالة لاستخراج الإحداثيات من رابط الخريطة
    const parseMapUrl = () => {
        const coordinates = extractCoordinatesFromMapUrl(formData.map_link);
        
        if (coordinates) {
            setFormData(prev => ({
                ...prev,
                lat: coordinates.lat.toString(),
                lng: coordinates.lng.toString()
                // لا نمسح map_link - نتركه كما هو
            }));
            showSuccessToast('تم استخراج الإحداثيات من رابط الخريطة بنجاح');
        } else {
            showErrorToast('لم يتم العثور على إحداثيات صحيحة في رابط الخريطة');
        }
    };


    // دالة نسخ كلمة المرور
    const copyPassword = async (password) => {
        if (!password) {
            showErrorToast('لا توجد كلمة مرور للنسخ');
            return;
        }

        try {
            await navigator.clipboard.writeText(password);
            showSuccessToast('تم نسخ كلمة المرور إلى الحافظة');
        } catch (error) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = password;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            showSuccessToast('تم نسخ كلمة المرور إلى الحافظة');
        }
    };

    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            showErrorToast('المتصفح لا يدعم الحصول على الموقع');
            return;
        }

        setLocationLoading(true);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setFormData(prev => ({
                    ...prev,
                    lat: latitude.toString(),
                    lng: longitude.toString()
                }));
                setLocationLoading(false);
                showSuccessToast('تم الحصول على الموقع بنجاح');
            },
            (error) => {
                setLocationLoading(false);
                let errorMessage = 'فشل في الحصول على الموقع';
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'تم رفض إذن الوصول للموقع';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = 'الموقع غير متاح';
                        break;
                    case error.TIMEOUT:
                        errorMessage = 'انتهت مهلة طلب الموقع';
                        break;
                }
                showErrorToast(errorMessage);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 60000
            }
        );
    };

    const validateForm = () => {
        if (!formData.market_name || !formData.phone || !formData.email || !formData.password || 
            !formData.password_confirmation || !formData.category_id || !formData.subcategory_id || 
            !formData.address || !formData.trader_name || !formData.contact_number || 
            !formData.lat || !formData.lng) {
            showErrorToast('يرجى ملء جميع الحقول المطلوبة');
            return false;
        }

        if (formData.password !== formData.password_confirmation) {
            showErrorToast('كلمة المرور وتأكيد كلمة المرور غير متطابقتين');
            return false;
        }

        if (formData.password.length < 6) {
            showErrorToast('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // مسح الأخطاء والرسائل السابقة والسماح بعرض الأخطاء الجديدة
        dispatch(clearError());
        dispatch(clearSuccessMessage());
        setAllowErrorToast(true);
        
        if (!validateForm()) {
            setShake(true);
            setTimeout(() => setShake(false), 500);
            return;
        }

        const submitData = {
            ...formData,
            phone: formData.phone.startsWith('+') ? formData.phone : `+20${formData.phone}`,
            contact_number: formData.contact_number.startsWith('+') ? formData.contact_number : `+2${formData.contact_number}`,
            fcm_token: 'default_token_value'
        };

        dispatch(storeRegister(submitData));
    };

    const LoadingSpinner = () => (
        <div className="spinner-border text-light" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    return (
        <div className="container-fluid d-flex p-0 auth-page"
            style={{
                minHeight: '100vh',
                background: 'white'
            }}>
            
            {/* النموذج في المنتصف */}
            <div className="col-12 d-flex justify-content-center align-items-center px-4 my-5">
                <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    className={`card shadow-lg ${shake ? 'shake-animation' : ''}`}
                    style={{
                        width: '100%',
                        maxWidth: '800px',
                        border: 'none',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        backgroundColor: 'white'
                    }}>

                    <div className="card-body p-4">
                        {/* اللوجو */}
                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: 'spring' }}
                            className="text-center mb-4">
                            <img 
                                src="/logokhadamat.png" 
                                alt="Khadamat Logo" 
                                style={{
                                    height: '80px',
                                    width: 'auto'
                                }}
                            />
                        </motion.div>

                        {/* عنوان إنشاء حساب متجر */}
                        <motion.h4
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="text-center mb-5 fw-bold"
                            style={{ color: '#333', fontSize: '20px' }}>
                            إنشاء حساب متجر
                        </motion.h4>

                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                {/* اسم المتجر */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        اسم المتجر *
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">
                                            <FaStore className="text-secondary" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="market_name"
                                            value={formData.market_name}
                                            onChange={handleInputChange}
                                            placeholder="اسم المتجر"
                                            required
                                            style={{ borderLeft: 'none' }}
                                        />
                                    </div>
                                </div>

                                {/* اسم التاجر */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        اسم التاجر *
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">
                                            <FaUser className="text-secondary" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="trader_name"
                                            value={formData.trader_name}
                                            onChange={handleInputChange}
                                            placeholder="اسم التاجر"
                                            required
                                            style={{ borderLeft: 'none' }}
                                        />
                                    </div>
                                </div>

                                {/* رقم الهاتف */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        رقم الهاتف *
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">
                                            <FaPhone className="text-secondary" />
                                        </span>
                                        <input
                                            type="tel"
                                            className={`form-control ${fieldErrors.phone ? 'is-invalid' : ''}`}
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            placeholder="Ex : 1xxxxxxxxx (1 + 10 أرقام)"
                                            required
                                            style={{ borderLeft: 'none' }}
                                        />
                                    </div>
                                    {fieldErrors.phone && (
                                        <div className="invalid-feedback d-block">
                                            {fieldErrors.phone}
                                        </div>
                                    )}
                                </div>

                                {/* رقم التواصل */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        رقم التواصل *
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">
                                            <FaPhone className="text-secondary" />
                                        </span>
                                        <input
                                            type="tel"
                                            className={`form-control ${fieldErrors.contact_number ? 'is-invalid' : ''}`}
                                            name="contact_number"
                                            value={formData.contact_number}
                                            onChange={handleInputChange}
                                            placeholder="Ex : 1xxxxxxxxx (1 + 10 أرقام)"
                                            required
                                            style={{ borderLeft: 'none' }}
                                        />
                                    </div>
                                    {fieldErrors.contact_number && (
                                        <div className="invalid-feedback d-block">
                                            {fieldErrors.contact_number}
                                        </div>
                                    )}
                                </div>

                                {/* البريد الإلكتروني */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        البريد الإلكتروني *
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">
                                            <FaEnvelope className="text-secondary" />
                                        </span>
                                        <input
                                            type="email"
                                            className="form-control"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="example@example.com"
                                            required
                                            style={{ borderLeft: 'none' }}
                                        />
                                    </div>
                                </div>

                                {/* الفئة */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        الفئة *
                                    </label>
                                    <select
                                        className="form-select"
                                        name="category_id"
                                        value={formData.category_id}
                                        onChange={handleInputChange}
                                        required>
                                        <option value="">اختر الفئة</option>
                                        {categories
                                            .filter(category => category.id !== 8 && category.id !== 12)
                                            .map(category => (
                                                <option key={category.id} value={category.id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                    </select>
                                </div>

                                {/* الفئة الفرعية */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        الفئة الفرعية *
                                    </label>
                                    <select
                                        className="form-select"
                                        name="subcategory_id"
                                        value={formData.subcategory_id}
                                        onChange={handleInputChange}
                                        required
                                        disabled={!formData.category_id}>
                                        <option value="">اختر الفئة الفرعية</option>
                                        {subcategories.map(subcategory => (
                                            <option key={subcategory.id} value={subcategory.id}>
                                                {subcategory.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* ساعات العمل */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        ساعات العمل
                                    </label>
                                    <div className="row">
                                        <div className="col-6">
                                            <input
                                                type="time"
                                                className="form-control"
                                                name="start_time"
                                                value={formData.start_time}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <input
                                                type="time"
                                                className="form-control"
                                                name="end_time"
                                                value={formData.end_time}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* العنوان */}
                                <div className="col-12 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        العنوان *
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">
                                            <FaMapMarkerAlt className="text-secondary" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                            placeholder="العنوان الكامل"
                                            required
                                            style={{ borderLeft: 'none' }}
                                        />
                                    </div>
                                </div>

                                {/* رابط الخريطة */}
                                <div className="col-12 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        رابط الخريطة
                                    </label>
                                    <div className="input-group">
                                        <input
                                            type="url"
                                            className="form-control"
                                            name="map_link"
                                            value={formData.map_link}
                                            onChange={handleInputChange}
                                            placeholder="https://maps.google.com/..."
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={parseMapUrl}
                                            disabled={!formData.map_link}
                                            title="استخراج الإحداثيات من رابط الخريطة"
                                        >
                                            <FaLocationArrow />
                                        </button>
                                    </div>
                                    <small className="text-muted">
                                        سيتم استخراج الإحداثيات تلقائياً عند إدخال رابط صحيح، أو يمكنك الضغط على الزر يدوياً
                                    </small>
                                </div>

                                {/* خط العرض وخط الطول */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        خط العرض *
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">
                                            <FaLocationArrow className="text-secondary" />
                                        </span>
                                        <input
                                            type="number"
                                            step="any"
                                            className="form-control"
                                            name="lat"
                                            value={formData.lat}
                                            onChange={handleInputChange}
                                            placeholder="30.0444"
                                            required
                                            style={{ borderLeft: 'none' }}
                                        />
                                    </div>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        خط الطول *
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">
                                            <FaLocationArrow className="text-secondary" />
                                        </span>
                                        <input
                                            type="number"
                                            step="any"
                                            className="form-control"
                                            name="lng"
                                            value={formData.lng}
                                            onChange={handleInputChange}
                                            placeholder="31.2357"
                                            required
                                            style={{ borderLeft: 'none' }}
                                        />
                                    </div>
                                </div>

                                {/* زر الحصول على الموقع تلقائياً */}
                                <div className="col-12 mb-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary w-100"
                                        onClick={getCurrentLocation}
                                        disabled={locationLoading}
                                        style={{
                                            borderRadius: '8px',
                                            padding: '12px',
                                            fontSize: '0.9rem',
                                            fontWeight: '500'
                                        }}>
                                        {locationLoading ? (
                                            <>
                                                <div className="spinner-border spinner-border-sm mx-2" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                جاري الحصول على الموقع...
                                            </>
                                        ) : (
                                            <>
                                                <FaLocationArrow className="mx-2" />
                                                الحصول على الموقع تلقائياً
                                            </>
                                        )}
                                    </button>
                                </div>

                                {/* الوصف */}
                                <div className="col-12 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        الوصف
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="3"
                                        placeholder="وصف المتجر..."
                                    />
                                </div>

                                {/* كلمة المرور */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        كلمة المرور *
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">
                                            <FaLock className="text-secondary" />
                                        </span>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            className="form-control"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            placeholder="********"
                                            required
                                            style={{ borderLeft: 'none' }}
                                        />
                                        <button
                                            type="button"
                                            className="input-group-text bg-light"
                                            onClick={() => setShowPassword(!showPassword)}
                                            style={{ cursor: 'pointer' }}
                                            title="إظهار/إخفاء كلمة المرور">
                                            {showPassword ? <FaEyeSlash className="text-secondary" /> : <FaEye className="text-secondary" />}
                                        </button>
                                        <button
                                            type="button"
                                            className="input-group-text bg-light"
                                            onClick={() => copyPassword(formData.password)}
                                            disabled={!formData.password}
                                            style={{ cursor: formData.password ? 'pointer' : 'not-allowed' }}
                                            title="نسخ كلمة المرور">
                                            <FaCopy className="text-secondary" />
                                        </button>
                                    </div>
                                </div>

                                {/* تأكيد كلمة المرور */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        تأكيد كلمة المرور *
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">
                                            <FaLock className="text-secondary" />
                                        </span>
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            className="form-control"
                                            name="password_confirmation"
                                            value={formData.password_confirmation}
                                            onChange={handleInputChange}
                                            placeholder="********"
                                            required
                                            style={{ borderLeft: 'none' }}
                                        />
                                        <button
                                            type="button"
                                            className="input-group-text bg-light"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            style={{ cursor: 'pointer' }}
                                            title="إظهار/إخفاء تأكيد كلمة المرور">
                                            {showConfirmPassword ? <FaEyeSlash className="text-secondary" /> : <FaEye className="text-secondary" />}
                                        </button>
                                        <button
                                            type="button"
                                            className="input-group-text bg-light"
                                            onClick={() => copyPassword(formData.password_confirmation)}
                                            disabled={!formData.password_confirmation}
                                            style={{ cursor: formData.password_confirmation ? 'pointer' : 'not-allowed' }}
                                            title="نسخ تأكيد كلمة المرور">
                                            <FaCopy className="text-secondary" />
                                        </button>
                                    </div>
                                </div>

                                {/* رفع الملفات */}
                                <div className="col-12 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        شعار المتجر
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        name="logo"
                                        onChange={handleInputChange}
                                        accept="image/*"
                                        required
                                    />
                                </div>


                                <div className="col-12 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        وثائق المتجر
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        name="market_document"
                                        onChange={handleInputChange}
                                        accept=".pdf,.doc,.docx"
                                    />
                                </div>
                            </div>

                            {/* زر إنشاء الحساب */}
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="submit"
                                className="btn w-100 text-white py-3 fw-medium"
                                style={{
                                    backgroundColor: '#184194',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontSize: '1.1rem',
                                    marginTop: '1rem'
                                }}
                                disabled={loading}>
                                {loading ? <LoadingSpinner /> : 'إنشاء الحساب'}
                            </motion.button>
                        </form>

                        {/* رابط العودة لتسجيل الدخول */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                            className="mt-3 text-center">
                            <button
                                type="button"
                                className="btn btn-link text-decoration-none"
                                onClick={() => navigate('/login')}
                                style={{ color: '#184194', fontSize: '0.9rem' }}>
                                لديك حساب بالفعل؟ تسجيل الدخول
                            </button>
                        </motion.div>
                    </div>
                </motion.div>
            </div>

        </div>
    );
};

export default StoreRegister;
